/**
 * 입력 항목 도움말.
 *
 * 설계자가 "여기에 무엇을 넣어야 하는가"를 바로 알 수 있어야 한다.
 * 문구를 한곳에 모아두는 이유는 **같은 항목이 여러 화면에 나오기 때문**이다
 * (예: 배압은 기체·수증기·액체·2상 화면에 모두 있다).
 * 화면마다 따로 쓰면 설명이 갈라지고, 한쪽만 고치는 실수가 난다.
 *
 * 단위 주의를 특히 강조한다. 기체·수증기·2상은 절대압, 액체는 게이지압을
 * 쓰는데 이게 실무에서 가장 자주 틀리는 지점이다.
 */
export const FIELD_HELP = {
  /* ── 밸브 공통 ─────────────────────────────────────────── */
  tag: "밸브 태그 번호입니다. P&ID 에 표기된 번호를 그대로 적으십시오. 예: PSV-101",
  equipment:
    "이 밸브가 보호하는 설비 이름입니다. 계산서와 PSM 명세서에 그대로 실립니다. 예: 탈에탄기 환류드럼",
  setPressure:
    "밸브 설정압(set pressure)을 게이지압 kPag 로 넣으십시오. 단일 설치에서는 설비의 MAWP(최고허용사용압력) 이하여야 합니다 (API 520 Part I 5.4.2.1.1).",
  installation:
    "이 설비를 보호하는 안전밸브가 하나면 단일, 둘 이상이면 복수입니다. 허용 과압이 달라집니다 — 단일 10 %, 복수 16 % (운전 상황 기준).",

  /* ── 시나리오 ─────────────────────────────────────────── */
  scenarioItem:
    "API 521 Table 1 의 과압 시나리오 17종 중 해당하는 것을 고르십시오. 조항 번호는 API 521 본문에서 유량 산정 방법을 찾을 때 씁니다.",
  scenarioDescription:
    "이 설비에서 구체적으로 어떤 상황인지 직접 적으십시오. 표준 항목명만으로는 근거가 남지 않습니다. 계산서와 PSM 명세서에 그대로 실립니다. 예: 하류 차단밸브 오조작으로 배출 경로 폐쇄, 공급 펌프는 계속 운전",
  contingency:
    "운전 중 이상(비화재)이면 '운전', 외부 화재면 '화재'입니다. 화재는 축적압 한계가 MAWP 121 % 라 허용 과압 21 % 가 적용됩니다 (API 520 Part I 5.4.3.1.1).",
  fluidKind:
    "배출되는 유체의 상태입니다. 상태에 따라 적용 계산식이 완전히 달라집니다.",
  includedCheck:
    "체크를 풀면 지배 케이스 비교에서 제외됩니다. 입력값은 지워지지 않으므로, 검토했으나 채택하지 않은 시나리오를 남겨둘 때 쓰십시오.",

  /* ── 유량 ─────────────────────────────────────────────── */
  flowMass:
    "소요 분출용량을 질량유량 kg/h 로 넣으십시오. 표준에 계산식이 없는 시나리오는 열·물질수지로 직접 구해야 합니다.",
  flowVolume:
    "소요 분출용량을 체적유량 L/min 으로 넣으십시오. 액체는 체적 기준입니다.",

  /* ── 기체·증기 ────────────────────────────────────────── */
  temperatureK:
    "배출 시점의 유입 온도를 절대온도 K 로 넣으십시오. (℃ + 273.15)",
  molecularWeight:
    "유체의 분자량입니다. 혼합물이면 조성 기준 평균 분자량을 쓰십시오. 공정 자료값을 쓰고, 표의 순물질 값을 그대로 쓰지 마십시오.",
  specificHeatRatio:
    "이상기체 비열비 k = Cp/Cv 입니다. ⚠ 공정 시뮬레이터가 주는 실기체 비열비를 쓰면 밸브가 작게 나옵니다 — 반드시 이상기체 값을 쓰십시오 (API 520 Part I 5.6.2.4).",
  compressibility:
    "배출 조건에서의 압축계수 Z 입니다. 계산값이 없으면 1.0 을 쓰십시오 (API 520 Part I 5.6.3.2.2).",

  /* ── 압력 ─────────────────────────────────────────────── */
  overpressure:
    "허용 과압(%)입니다. 운전 상황은 단일 설치 10 % · 복수 설치 16 %, 외부 화재는 21 % 입니다 (API 520 Part I 5.4).",
  backpressureGauge:
    "총 배압을 게이지압 kPag 로 넣으십시오. 중첩배압(밸브가 열리기 전부터 걸린 압력) + 축적배압(배출 중 배관에서 생기는 압력)의 합입니다.",
  backpressureAbs:
    "총 배압을 절대압 kPa 로 넣으십시오. 중첩배압 + 축적배압 + 대기압입니다. ⚠ 게이지압이 아닙니다.",

  /* ── 보정계수 ─────────────────────────────────────────── */
  valveType:
    "일반형(Conventional)은 배압에 영향을 받고, 밸런스드 벨로우즈는 벨로우즈로 배압을 상쇄합니다. 파일럿형은 일반형과 같은 식을 씁니다. ⚠ 벨로우즈는 배압이 임계압을 넘어도 임계유동식을 씁니다 (5.6.4.3).",
  kbBellows:
    "배압 보정계수 Kb 입니다. 밸런스드 벨로우즈에만 적용됩니다. 프로그램이 추정하지 않으니 제작사 자료(또는 API 520 Part I Figure 31)에서 읽어 넣으십시오. 비워두면 1.0 으로 계산하고 경고를 띄웁니다.",
  kwBellows:
    "액체용 배압 보정계수 Kw 입니다. 밸런스드 벨로우즈에만 적용됩니다. API 520 Part I Figure 32 또는 제작사 자료에서 읽어 넣으십시오.",
  kbLiquidBellows:
    "액체용 배압 보정계수 Kb 입니다. 밸런스드 벨로우즈에만 적용됩니다. API 520 Part I Figure C.3 또는 제작사 자료에서 읽어 넣으십시오.",
  kc: "파열판 조합 보정계수 Kc 입니다. 파열판이 없으면 1.0, 안전밸브 상류에 파열판이 있고 그 조합의 인증값이 없으면 0.9 입니다 (API 520 Part I 5.12.2).",
  kvTwoPhase:
    "점도 보정계수 Kv 입니다. 액상 점도가 100 cP(0.1 Pa·s) 이하이면 1.0 을 쓰십시오.",
  kpNonCertified:
    "과압 보정계수 Kp 입니다. 25 % 과압이면 1.0, 10 % 과압이면 0.6 입니다. 그 외 값은 API 520 Part I Figure 39 에서 읽으십시오 (5.9.2).",

  /* ── 수증기 ───────────────────────────────────────────── */
  steamState:
    "포화증기면 과열 보정 KSH = 1.0 이라 온도가 필요 없습니다. 과열증기면 배출 온도로 Table 13 에서 KSH 를 찾습니다.",
  steamTemperatureC:
    "과열증기의 배출 온도를 ℃ 로 넣으십시오. 1200 °F(약 649 ℃)를 넘으면 수증기식이 아니라 기체 사이징식을 써야 합니다 (API 520 Part I 5.7.1).",

  /* ── 액체 ─────────────────────────────────────────────── */
  certification:
    "ASME 용량인증을 받은 밸브면 '인증'(Kd 0.65), 인증이 없거나 불필요하면 '비인증'(Kd 0.62)입니다. 비인증 방식은 10 % 과압 적용 시 통상 과대설계가 됩니다 (5.9.2).",
  specificGravity:
    "배출 온도에서의 액체 비중입니다. 표준조건 물을 1.0 으로 봅니다.",
  viscosityKind:
    "점도 단위를 고르십시오. 절대점도(cP) 또는 Saybolt universal seconds(SSU)를 쓸 수 있습니다. 점도보정이 필요 없으면 '점도보정 안 함'을 고르십시오.",
  viscosityValue:
    "배출 온도에서의 점도입니다. 100 cP 이하이면 보정하지 않습니다(Kv = 1.0). 그보다 크면 레이놀즈수를 구해 Kv 를 적용하고, 결과가 선정 오리피스를 넘으면 자동으로 한 단계 키워 다시 계산합니다 (5.8.1.4~5).",

  /* ── 2상 유동 (입구 2상) ──────────────────────────────── */
  inletSpecificVolume:
    "PRV 입구 조건에서 2상 혼합물의 비체적 v₁ (m³/kg)입니다. 공정 시뮬레이터에서 얻으십시오.",
  flashSpecificVolume:
    "입구 압력 P₁ 의 90 % 에서 플래시 계산한 비체적 v₉ (m³/kg)입니다. 등엔트로피 플래시가 원칙이나, 임계점에서 먼 저건도 혼합물은 등엔탈피(단열) 플래시로 충분합니다 (API 520 Part I 식 C.12).",
  criticalRatioMethod:
    "임계 압력비를 정의식의 수치해로 구할지, 명시적 근사식으로 구할지 고릅니다. 표준서는 둘 다 인정하며 차이는 1 % 미만입니다. 기본값(정의식)을 권장합니다.",

  /* ── 2상 유동 (입구 과냉 액체) ────────────────────────── */
  inletLiquidDensity:
    "PRV 입구에서의 액체 밀도 ρl₁ (kg/m³)입니다.",
  flashDensity:
    "포화압 Ps 의 90 % 에서 플래시 계산한 밀도 ρ₉ (kg/m³)입니다. ⚠ 입구가 2상인 경우와 달리 여기서는 비체적이 아니라 밀도를 넣습니다 (API 520 Part I 식 C.30).",
  saturationPressure:
    "입구 온도 T₁ 에 대응하는 포화(증기)압을 절대압 kPa 로 넣으십시오. 다성분계는 T₁ 에서의 기포점압을 씁니다.",

  /* ── 오리피스 선정 (개별 계산기) ──────────────────────── */
  requiredArea:
    "API 520 사이징식으로 구한 소요 유효 오리피스 면적입니다. 이 값 이상인 최소 표준 오리피스를 선정합니다.",
  areaUnit: "입력한 면적의 단위입니다. mm² 와 in² 중에 고르십시오.",

  /* ── 소요 분출용량 계산기 ─────────────────────────────── */
  rateCalcKind:
    "이 시나리오에서 쓸 산정 방법입니다. 화재는 용기 안에 액체가 있는지 기체만 있는지에 따라 계산식이 완전히 달라지고, 튜브 파열은 새어 나오는 유체가 액체인지 기체인지에 따라 갈립니다.",
  pdRatedFlow:
    "펌프·압축기 데이터시트의 정격 토출량입니다. 정변위 기계는 배압과 무관하게 이 유량을 그대로 밀어내므로, 토출측이 막히면 소요 분출용량은 이 값 자체입니다(KOSHA GUIDE D-18-2020 4.1(1)(다)).",
  pdDensity:
    "배출 조건(설정압 부근)에서의 액체 밀도입니다. 정격 토출량(체적)에 곱해 질량유량을 구합니다.",
  pdSuctionPressure:
    "압축기 흡입측 압력입니다. 정격 토출량이 흡입 조건(실제 부피) 기준이므로, 이 압력·온도·분자량으로 흡입측 밀도를 구해 질량유량으로 환산합니다.",
  pdSuctionTemperature:
    "압축기 흡입측 온도(K)입니다. 흡입측 밀도를 구하는 데 씁니다.",
  pdMolecularWeight: "흡입 기체의 분자량입니다.",
  pdCompressibility:
    "흡입 조건에서의 압축계수 Z 입니다. 모르면 1.0을 쓰십시오.",
  pdCapacityFactor:
    "기본값 1.0을 쓰십시오. 1 미만으로 낮추는 것은 표준 근거가 아닌 사용자 판단입니다 — 우회(bypass) 라인 등으로 실제 필요 용량이 줄어드는 근거가 있을 때만 낮추고, 그 근거를 계산서·PSM에 반드시 남기십시오. 이미 선정된 밸브 규격에 맞추기 위해 소요량 쪽을 낮추는 것은 설계 순서가 거꾸로 된 것입니다.",
  pipeInnerDiameter:
    "배관 최대유입을 검토할 배관의 내경입니다.",
  pipeVelocity:
    "이 배관에 적용하는 사내 설계기준 유속입니다. KOSHA·API 표준이 정한 값이 아니므로, 유속의 출처(사내 기준 문서 등)를 계산서에 남기십시오.",
  pipeDensity:
    "배출 조건에서의 유체 밀도입니다.",
  phInnerDiameter:
    "최대유입 경로로 볼 배관의 내경입니다. 이 배관 자체의 실제 수력학(마찰손실)으로 유량을 역산합니다.",
  phStraightLength:
    "이 배관의 직관 길이입니다. 부속은 아래 등가길이에 따로 넣으십시오.",
  phFittingLength:
    "부속(엘보·티·밸브 등) 등가길이의 합입니다. 배관 검토 탭의 부속 목록과 같은 방식으로 미리 환산해 넣으십시오.",
  phRoughness:
    "배관 재질의 절대조도입니다. 배관 검토 탭의 재질·조도표(KOSHA D-63 <표2>)와 같은 값을 쓰십시오.",
  phUpstreamPressure:
    "이 배관을 밀어 넣는 쪽(구동측, 상류)의 압력입니다 — 보통 상류 설비의 운전압입니다.",
  phDownstreamPressure:
    "이 배관이 흘러 들어가는 쪽(받는측, 하류)의 압력입니다 — 보통 보호 대상 설비의 배출 조건 압력입니다.",
  phDensity: "배출 조건에서의 유체 밀도입니다.",
  phViscosity:
    "배출 조건에서의 절대점도를 cP 로 넣으십시오. 1 cP = 0.001 Pa·s.",
  cvValue:
    "밸브 제작사 데이터시트의 Cv(유량계수) 값입니다. 고장(완전개방) 상태에서 이 Cv 로 실제 통과유량을 구합니다 — ANSI/ISA-75.01.01 · IEC 60534-2-1 의 밸브 사이징식을 유량에 대해 거꾸로 푼 것입니다.",
  cvUpstreamPressure:
    "고장난(완전 개방된) 밸브의 상류(감압 전) 압력입니다.",
  cvDownstreamPressure:
    "고장난 밸브의 하류(감압 후) 압력입니다 — 보통 보호 대상 설비의 정상 운전압입니다.",
  cvDensity: "상류 조건에서의 액체 밀도입니다.",
  cvFL:
    "밸브의 정격 압력회복계수 FL 입니다. 초크(공동현상) 판정에 씁니다. 제작사 유량계수표에 없으면 글로브 밸브 통상값 0.8~0.9 를 쓰고 근거를 남기십시오.",
  cvVaporPressure:
    "배출 온도에서의 액체 증기압(절대압)입니다. 초크가 플래싱 때문인지 판정하는 데 씁니다. 모르면 0 으로 두면 보수적(초크 안 됨 쪽)입니다.",
  cvCriticalPressure:
    "유체의 열역학적 임계압력(절대압)입니다. 비워두면 무한대로 보고(FF=0.96) 계산합니다 — 값을 넣으면 초크 압력차가 더 정확해집니다.",
  cvTemperature: "밸브 상류(고장 지점 앞) 유체 온도입니다.",
  cvMolecularWeight: "밸브 상류 유체의 분자량입니다.",
  cvSpecificHeatRatio: "밸브 상류 유체의 비열비 k 입니다.",
  cvCompressibility: "밸브 상류 조건에서의 압축계수 Z 입니다. 모르면 1.0을 쓰십시오.",
  cvXt:
    "밸브의 정격 압력강하비 계수 xT 입니다. 초크(임계유동) 판정에 씁니다. 제작사 유량계수표 값을 쓰는 것이 정확하며, 없으면 글로브 밸브 통상값 0.7(회전형 밸브는 0.15~0.3)을 쓰고 근거를 남기십시오.",
  valveInnerDiameter:
    "고장난(완전 개방된) 제어밸브 또는 체크밸브가 있는 배관의 내경입니다. 이 단면적을 오리피스로 보고 유량을 구합니다.",
  valveUpstreamPressure:
    "밸브 고장으로 유체가 흘러 들어오는 쪽(상류, 감압 전)의 압력입니다.",
  valveDownstreamPressure:
    "밸브를 지나 보호 대상 설비로 들어가는 쪽(하류, 감압 후)의 압력입니다 — 보통 설비의 정상 운전압입니다.",
  valveDensity:
    "상류 조건에서의 액체 밀도입니다.",
  valveCd:
    "배출계수입니다. 표준이 값을 정하지 않으므로 기본 1.0(보수적)을 쓰거나 사내 기준값을 넣고 근거를 남기십시오.",
  valveTemperature:
    "상류(고장난 밸브 앞) 유체 온도입니다.",
  valveMolecularWeight:
    "상류 유체의 분자량입니다.",
  valveSpecificHeatRatio:
    "상류 유체의 비열비 k 입니다.",
  valveCompressibility:
    "상류 조건에서의 압축계수 Z 입니다. 모르면 1.0을 쓰십시오.",
  flowsheetInput:
    "이 노드가 받아오는 상류 스트림입니다. 목록에 없으면 그 스트림을 만드는 노드를 먼저 추가하십시오.",
  flowsheetPhase:
    "이 소스가 기체(수분 포함 가능)인지 액체인지 고르십시오. 조성이 고정된 스트림만 정확합니다 — 상변화가 있는 구간은 스크러버 노드를 쓰십시오.",
  flowsheetTemperature: "이 지점의 온도입니다.",
  flowsheetPressure: "이 지점의 압력입니다.",
  flowsheetDryGasFlow: "물을 뺀 나머지 성분을 하나로 뭉친 건가스의 질량유량입니다.",
  flowsheetWaterVapor: "가스에 섞여 있는 수증기의 질량유량입니다. 건조 가스면 0으로 두십시오.",
  flowsheetDryGasCp: "건가스의 정압비열입니다. 공기는 약 1005 J/kg·K 입니다.",
  flowsheetViscosity: "이 유체의 절대점도를 cP 로 넣으십시오. 1 cP = 0.001 Pa·s.",
  flowsheetLiquidCp: "이 액체의 비열입니다. 물은 약 4186 J/kg·K 입니다.",
  flowsheetFittingK:
    "부속 손실계수의 합입니다. 액체 구간에만 적용됩니다 — 기체 구간은 KOSHA 등온식 특성상 부속을 등가길이에 이미 포함해야 하므로, 부속 등가길이 쪽에 더해 넣으십시오.",
  flowsheetDuty:
    "이 지점에서 더하거나 빼는 열량입니다. 양수면 가열, 음수면 냉각입니다. 상변화는 계산하지 않습니다 — 냉각으로 응축이 생길 것 같으면 대신 스크러버 노드를 쓰십시오.",
  flowsheetSaturation:
    "출구 가스가 포화 상태의 몇 %까지 가는지입니다. 기본 100%(완전 포화)를 씁니다. 실제 접촉 효율이 낮다고 보면 낮추십시오.",
  flowsheetMakeupWater:
    "가스를 데우며 포화시키는 데 필요한 물이 이 스크러버로 공급될 수 있는 최대량입니다. 냉각(응축) 방향이면 쓰이지 않습니다. 부족하면 경고가 뜹니다.",
  flowsheetValveDrop: "이 밸브(또는 조리개)를 지나며 생기는 고정 압력강하입니다.",
  flowsheetCompositionMode:
    "'평균값 직접 입력'은 지금까지 쓰던 방식(분자량·비열·점도를 미리 구해서 넣기)입니다. '성분 조성으로 입력'을 고르면 몰% 목록만 넣어도 이 값들을 자동으로 구합니다.",
  flowsheetOreFe2O3:
    "광석 중 Fe2O3(산화철) 질량유량입니다. 다른 산화철 형태(FeO, Fe3O4 등)나 맥석(gangue)은 포함하지 않습니다.",
  flowsheetTargetMetallization:
    "목표 금속화율(금속 Fe / 전체 Fe × 100, %)입니다. 공급 H2 가 부족하면 이 목표에 못 미칠 수 있고, 그러면 경고가 뜹니다.",
  flowsheetAchievedMetallization:
    "이 광석이 ESF 에 들어오기 전 실제로 도달한 금속화율(%)입니다 — 환원로 결과의 '도달 금속화율' 값을 그대로 옮겨 적으십시오.",
  flowsheetCarbonFeed:
    "ESF 에 공급하는 탄소량입니다. 미환원 FeO 를 다 반응시키기에 부족하거나 남으면 경고가 뜹니다.",
  wettedGeometry:
    "젖은 면적을 형상에서 자동으로 구합니다. 헤드·스커트·연결 배관 표면을 포함할지는 설계자가 판단해야 하므로, 그런 면적을 더하려면 '직접 입력'을 고르십시오.",
  vesselDiameter: "용기 안지름을 m 로 넣으십시오.",
  vesselLength: "수평 원통 동체의 접선간 길이를 m 로 넣으십시오. 헤드는 포함하지 않습니다.",
  liquidHeight:
    "화재 시 예상되는 액위를 바닥에서부터 m 로 넣으십시오. 지면에서 7.6 m 를 넘는 부분은 자동으로 제외됩니다 (API 521 Table 4).",
  wettedArea:
    "총 습윤면적을 m² 로 직접 넣으십시오. 화염이 닿을 수 있는 높이(지면에서 7.6 m)까지만 포함해야 합니다.",
  environmentFactor:
    "API 521 Table 5 의 환경계수입니다. 나관 1.0, 보온 두께에 따라 0.3~0.026, 복토 0.03. ⚠ 보온이 없는 용기에는 이 계수를 적용하지 않습니다 — '보온 있음'을 끄면 자동으로 1.0 이 적용됩니다.",
  insulated:
    "용기에 화재 등급 보온이 되어 있으면 켜십시오. 꺼져 있으면 표준 각주에 따라 환경계수가 1.0 으로 강제됩니다 (API 521 Table 5 각주 e).",
  drainage:
    "가연성 액체와 소방수를 용기에서 멀리 흘려보낼 수 있고 소방 대응이 즉시 가능하면 켜십시오. 켜면 식 (7) 상수 43,200, 끄면 식 (8) 상수 70,900 이 적용됩니다. 판단 근거를 문서로 남기십시오.",
  latentHeat:
    "배출 조건(압력)에서의 증발잠열을 J/kg 로 넣으십시오. 흡수열량을 이 값으로 나눠 증기 발생량을 구합니다. 예: 경질 탄화수소 약 300,000~400,000 J/kg.",
  exposedArea:
    "화염에 노출되는 용기 표면적을 m² 로 넣으십시오. 액체가 없는 기체 용기이므로 습윤면적이 아니라 노출 표면적입니다.",
  normalPressure: "화재 발생 전 정상 운전 압력입니다. 게이지압으로 넣으십시오.",
  normalTemperature: "정상 운전 온도를 K 로 넣으십시오. 배출 시점의 기체 온도를 여기서 환산합니다 (식 12).",
  wallTemperature:
    "용기 재질이 견딜 수 있는 최대 벽면 온도를 K 로 넣으십시오. 탄소강은 통상 866 K(593 ℃)를 씁니다. 재질과 설계 기준에 맞춰 조정하십시오.",
  useDefaultFPrime:
    "벽면 온도·운전 조건을 확보하지 못했을 때 켜십시오. 식 (10) 대신 표준이 제시한 기본값 F′ = 821 을 씁니다. 값을 알면 끄는 편이 정확합니다.",
  alphaV:
    "배출 조건에서 액체의 체적팽창계수(1/℃) — 온도가 1℃ 오를 때 부피가 늘어나는 비율입니다. 공정 설계자료가 가장 정확하며, 없으면 아래 표에서 °API 도(원유·석유제품 비중 단위, 클수록 가벼움)로 고르십시오. 자세한 설명은 아래 안내문을 참고하십시오.",
  heatRate:
    "갇힌 액체에 들어오는 총 전열량을 W 로 넣으십시오. 열교환기는 운전 중 최대 duty 를 씁니다. 태양복사만 받는 배관은 표면적 × 복사열유속으로 구합니다.",
  relativeDensity: "15.6 ℃ 물을 1.00 으로 한 비중입니다. 표준이 액체의 압축성은 무시합니다.",
  specificHeatCapacity: "갇힌 액체의 비열을 J/kg·K 로 넣으십시오. 예: 물 4,184, 경질 탄화수소 약 2,000~2,300.",
  tubeInnerDiameter:
    "파단된 튜브의 안지름을 mm 로 넣으십시오. 예: 3/4″ 16BWG 튜브는 19.86 mm. 표준은 튜브 하나가 튜브시트 뒷면에서 예리하게 절단되고, 유체가 양쪽 끝으로 나온다고 봅니다 — 개구부 2개소가 자동 적용됩니다.",
  highSidePressure:
    "고압측(튜브측) 운전 압력을 게이지압으로 넣으십시오. 저압측 압력은 이 밸브의 배출 압력이 자동으로 쓰입니다.",
  tubeDensity: "고압측 액체의 밀도를 kg/m³ 로 넣으십시오.",
  /* ── 배관 압력손실 ────────────────────────────────────── */
  pipeNps:
    "배관 호칭경입니다. ⚠ 입구 배관은 밸브 입구 호칭경보다 작으면 안 됩니다 (API 520 Part II 7.3.2). 출구 배관도 밸브 출구보다 작게 하지 않습니다.",
  pipeSchedule:
    "배관 스케줄입니다. 두꺼울수록 내경이 작아져 압력손실이 커집니다. 사내 배관 사양의 값을 쓰십시오.",
  pipeMaterial:
    "절대조도를 정합니다. 값은 **KOSHA GUIDE D-63-2018 <표2>** 기준입니다. ⚠ 표준은 시간이 지나며 내면이 나빠지는 것을 고려하라고 합니다 (API 520 Part II 7.3.7.2 a). 신관 값만 쓰지 마시고, 부식성 유체나 오래된 배관은 부식 항목을 검토하십시오.",
  customInnerDiameter:
    "사내 배관 사양이 이 표와 다르면 내경을 직접 넣으십시오. 여기 표는 ASME B36.10M 상용 치수이며 API 표준이 아닙니다.",
  straightLength:
    "부속을 뺀 순수 직관 길이를 m 로 넣으십시오. 부속은 아래 목록에서 따로 세면 등가길이로 환산됩니다.",
  fittingList:
    "배관에 달린 부속을 종류별로 세어 넣으십시오. ⚠ 용기에서 배관으로 들어가는 입구 손실은 표준이 반드시 포함하라고 합니다 (API 520 Part II 7.3.7.2 a). L/D 값은 **KOSHA GUIDE D-63-2018 <표1>** 기준입니다 — API 표준에는 이 표가 없습니다. 사내 기준이나 Crane TP-410 이 있으면 그쪽을 우선하십시오.",
  mawpPiping:
    "보호 대상 설비의 최고허용사용압력(MAWP)입니다. ⚠ 설정압과 다릅니다. 허용 축적배압이 설정압이 아니라 MAWP 기준으로 정해지므로, 설정압을 MAWP 보다 낮게 잡으면 여유가 그만큼 커집니다 (API 520 Part I 식 1).",
  allowableAccumulation:
    "허용 축적(%)입니다. 운전 단일 설치 10, 복수 설치 16, 외부 화재 21 을 씁니다.",
  valveTypePiping:
    "밸브 형식에 따라 배압 판정 기준이 완전히 달라집니다. 일반형은 축적배압 한계, 벨로우즈는 총 배압이 설정압의 50 % 이내가 통상 기준입니다 (5.3.3.2.1).",
  pipingFlow:
    "배관 검토에 쓸 유량을 kg/h 로 넣으십시오. 아래 '유량 기준'에서 정격 용량과 소요 유량 중 어느 것인지 고르십시오.",
  flowBasis:
    "정격 용량(rated)은 선정한 밸브가 실제로 흘릴 수 있는 최대 유량이고, 소요 유량(required)은 시나리오에서 요구되는 유량입니다. ⚠ 표준은 입구 손실 검토에 정격 용량을 쓰는 것을 원칙으로 합니다 (7.3.7.3) — 오리피스가 소요보다 크면 실제로 더 많이 흐르기 때문입니다.",
  pipingDensity:
    "배출 조건에서의 유체 밀도를 kg/m³ 로 넣으십시오. 기체는 배출 압력·온도에서의 값입니다.",
  pipingViscosity:
    "배출 조건에서의 절대점도를 cP(센티푸아즈)로 넣으십시오. 1 cP = 0.001 Pa·s. 기체는 통상 0.01~0.03 cP 입니다.",
  outletMolecularWeight:
    "출구 배관을 흐르는 유체의 분자량입니다. 등온 압축성 계산은 밀도를 pM/(ZRT) 로 지점마다 다시 구하므로, 입력란의 밀도 대신 이 값을 씁니다. 혼합가스면 몰분율 가중 평균(KOSHA GUIDE D-63-2018 식 (5-2))을 쓰십시오.",
  outletTemperatureK:
    "배출되는 유체의 온도(절대온도 K)입니다. KOSHA GUIDE D-63-2018 4.1(1) 은 배출배관을 등온 과정으로 보므로, 이 온도가 배관 전체에 그대로 적용됩니다. 배출 시점의 온도를 넣으십시오 — 상온이 아닙니다.",
  outletCompressibility:
    "배출 조건에서의 압축계수 Z 입니다. 저압 기체는 1.0 에 가깝고, 고압이거나 임계점 부근이면 0.7~0.95 정도가 됩니다. 모르면 1.0 을 넣되 계산서에 그렇게 가정했다고 남기십시오.",
  lossMethod:
    "부속 손실을 등가길이(L/D)로 환산할지 저항계수(K)로 다룰지 고릅니다. 표준은 방식을 지정하지 않으므로 둘 다 인정되지만, 계산서에 어느 쪽을 썼는지 반드시 남겨야 합니다.",
  inletExemption:
    "3 % 기준의 예외입니다. 열팽창 전용 밸브(7.3.8)와 원격감지 파일럿형(7.3.9)은 이 기준을 적용하지 않습니다. 해당하지 않으면 '해당 없음'으로 두십시오.",
  ruptureDiskDrop:
    "안전밸브 상류에 파열판이 있으면 그 압력강하를 넣으십시오. 표준이 입구 손실에 반드시 더하라고 합니다 (7.3.7.2 b). 없으면 0 입니다.",
  superimposed:
    "밸브가 열리기 전부터 출구쪽에 걸려 있는 압력입니다. 헤더 압력이나 하류 계통 압력이 여기 해당합니다. 대기 방출이면 0 입니다.",
  blowdown:
    "밸브가 닫히기 시작하는 압력과 설정압의 차입니다. 제작사 자료에서 얻으십시오. 통상 설정압의 7 % 정도입니다.",
  forceBalanceOverpressure:
    "힘 평형 판정에 쓸 과압입니다. 표준은 이 판정에 10 % 과압을 쓰라고 합니다 — 화재 21 % 가 아닙니다 (7.3.6 d).",
  reactionEnabled:
    "대기로 직접 방출하는 배관의 반력을 계산합니다. 배관 지지 설계에 필요합니다. 닫힌 계통(플레어 헤더)으로 보내면 해당하지 않습니다.",
  stagnationTemperature:
    "배관 출구에서의 정체온도를 K 로 넣으십시오. 배출온도를 그대로 쓰면 반력이 과대평가되는데, 표준은 그 방향이 보수적이라 반력 산정에는 허용합니다.",
  reactionOutletId:
    "방출점(배관 끝) 내경을 mm 로 넣으십시오. 이 면적에 정압이 곱해져 반력의 한 몫이 됩니다.",
  reactionStaticPressure:
    "대기로 팽창하기 직전, 배관 안의 정압입니다. 초크 유동이면 대기압보다 높습니다.",
  usedRelievingTemperature:
    "정체온도를 구하지 못해 배출온도를 넣으셨다면 켜십시오. 결과가 보수적(크게)이라는 안내가 붙습니다.",

  /* ── 플레어 헤더 ──────────────────────────────────────── */
  flareScenario:
    "어떤 동시 분출 상황을 보는지 적으십시오. 예: 전 공장 정전, A구역 화재. 표준은 헤더 배압을 정전·냉각수 상실·계장공기 상실 같은 **여러 밸브가 함께 터지는 상황** 기준으로 정하라고 합니다 (API 521 5.5.2).",
  tipPressure:
    "플레어 팁(계통 출구)의 압력을 게이지압으로 넣으십시오. 대기압 위로 팁 자체의 압력강하만큼입니다. 팁 용량과 압력강하는 플레어 벤더 자료에서 얻어야 합니다 — 플랜지 크기만으로는 알 수 없습니다 (5.5.4).",
  machLimit:
    "헤더·테일파이프의 마하수 상한입니다. API 521 은 값을 정하지 않고 사용자가 기준을 세우도록 하지만(5.5.3), **KOSHA GUIDE D-63-2018 은 숫자를 제시합니다** — 마하수가 1 이상이면 초음속이 되어 진동·소음이 심하므로 **0.8 미만**이 되도록 관경을 키우고, **가능한 한 0.6 이하**로 유지하는 것이 바람직하다(5.2.6.2). 입구측 마하수도 0.8 을 넘으면 관경을 키우라고 합니다(5.2.6.6). 기본값 0.5 는 그보다 안전한 쪽입니다. 여기 마하수는 등온 음속 기준이라 초크 한계가 1.0 입니다.",
  fittingAllowance:
    "부속 등가길이를 직관 길이의 몇 %로 뭉뚱그릴지입니다. 예비 검토에서 씁니다. 표준은 실제 배치 기준으로 **직관 길이의 100 % 이상**이 되는 경우가 흔하다고 합니다 (5.5.9). 아래 부속 목록과 함께 쓰면 둘 다 더해지니 중복해서 넣지 마십시오.",
  flareNode:
    "이 밸브가 어느 헤더 구간에 붙는지 고르십시오. 그 구간의 상류 끝에서 합류합니다. 팁에서 멀수록 지나야 할 헤더가 길어져 배압이 높아집니다.",
  flareDownstream:
    "이 구간이 흘러 나가는 곳입니다. 다른 구간을 고르면 그 구간의 상류 끝으로 합류하고, '플레어 팁'을 고르면 팁에 직접 닿습니다. ⚠ 팁에 닿는 구간은 하나뿐이어야 하며, 연결이 서로를 가리키며 돌면(순환) 계산이 거부됩니다.",
  flareViscosity:
    "이 밸브에서 나오는 기체의 절대점도를 cP 로 넣으십시오. 레이놀즈수를 구해 마찰계수를 정하는 데 씁니다 — **KOSHA GUIDE D-63-2018 5.2.2 (라) 가 요구하는 필수 물성**입니다. 기체는 통상 0.01~0.03 cP 입니다. 합류 지점에서는 몰분율·√분자량 가중으로 섞습니다 (식 5-4).",
  flareValveFlow:
    "이 시나리오에서 이 밸브가 흘리는 유량입니다. 동시에 터지지 않는 밸브는 0 으로 두십시오. 표준은 정격 용량이 아니라 **함께 일어날 수 있는 소요 유량의 합**으로 헤더를 사이징하는 것이 보통이라고 합니다 (5.5.3).",

  tubeDischargeCoefficient:
    "파단 단부의 배출계수입니다. ⚠ 표준이 값을 규정하지 않습니다. 날카로운 모서리 오리피스 기준 0.61 을 기본으로 두었으며, 사내 기준이 있으면 그 값을 쓰고 근거를 남기십시오.",

  /* ── 플레어 복사열·높이 (API 521 Annex F.2/C.2.2) ── */
  flrMassFlow: "플레어로 가는 가스의 질량유량입니다. 팁 유출속도(바람틸트 계산용)와, 자동계산 모드에서는 발열량도 이 값에서 구합니다.",
  flrMolecularWeight: "플레어 가스의 평균 분자량입니다. 팁 조건에서의 체적유량 환산에 씁니다.",
  flrTemperature: "플레어 팁에서의 가스 온도입니다.",
  flrCompressibility: "팁 조건에서의 압축계수 Z 입니다. 모르면 1.0을 쓰십시오.",
  flrTipPressure: "플레어링 중 팁 내부의 절대압력입니다.",
  flrTipDiameter: "플레어 팁의 내경입니다. 벤더 자료 또는 기존 설비 사양에서 가져오십시오.",
  flrWindVelocity: "화염 기울어짐 계산에 쓰는 설계 풍속입니다. API 521 은 수신점 방향으로 부는 9 m/s(20 mph, 팁 높이 기준)를 전형적인 가정으로 제시합니다(F.2) — 다른 지역 조건이 있으면 바꾸십시오.",
  flrHeatReleaseMode: "'직접 입력'은 발열량(Q)을 이미 알 때, '질량유량×LHV 자동계산'은 저위발열량만 알 때 씁니다. 둘 다 화염길이·복사거리 계산에 그대로 쓰입니다.",
  flrHeatReleaseDirect: "발열량 Q 를 직접 입력합니다(저위발열량 기준, kW).",
  flrLhv: "플레어 가스의 저위발열량(LHV)입니다. Q = 질량유량 × LHV 로 자동 계산됩니다.",
  flrFFactorMode: "'표에서 조회'를 고르면 가스 종류·버너직경으로 Table F.1 에서 F 를 자동으로 찾습니다(선형보간). 표에 맞는 가스가 없으면 '직접 입력'을 쓰십시오.",
  flrFFactorGas: "Table F.1(API 521)에 있는 4가지 대표 가스 중 플레어 가스와 가장 가까운 것을 고르십시오.",
  flrBurnerDiameter: "F-factor 조회에 쓰는 버너(팁) 직경입니다 — 위의 팁 내경(m)과 같은 값을 cm 단위로 넣으면 됩니다.",
  flrFFactorDirect: "복사열 분율 F(0~1)를 직접 입력합니다. Table F.1 조회 대신 제작사 자료 등 다른 근거가 있을 때 쓰십시오.",
  flrTransmissivity: "대기 투과율 τ(0~1)입니다. 모르면 API 521 예제처럼 1.0(감쇠 없음)을 쓰십시오.",
  flrAllowableRadiation: "이 지점에서 넘지 않아야 할 방사강도 K 입니다. 아래 Table 12 기준값 버튼을 눌러 채울 수 있습니다.",
  flrGradeDistance: "플레어 스택에서 검토 대상 지점(부지경계·인접설비 등)까지의 지면 수평거리입니다.",

  /* ── 플레어 복사열·높이 (KOSHA GUIDE C-C-18-2026, Brzustowski-Sommer 법) ── */
  bsLelPrime: "플레어 가스의 보정 전 폭발하한계 계수 CL' 입니다. KOSHA 지침의 유체별 표 또는 벤더 자료에서 가져오십시오.",
  bsMolecularWeight: "플레어 가스의 평균 분자량입니다.",
  bsMassFlow: "플레어로 가는 가스의 질량유량입니다.",
  bsHeatingValue: "플레어 가스의 연소열량(HHV 기준, kcal/kg)입니다. 발열량 Q = 이 값 × 질량유량으로 계산됩니다.",
  bsTipPressure: "플레어 팁에서의 절대압력입니다.",
  bsTipTemp: "플레어 팁에서의 가스 온도입니다.",
  bsAmbientTemp: "대기온도입니다.",
  bsWindVelocity: "설계 풍속입니다.",
  bsAirMolecularWeight: "공기의 평균 분자량입니다(보통 28.97).",
  bsCompressibility: "팁 조건에서의 압축계수 Z 입니다. 모르면 1.0을 쓰십시오.",
  bsMachNumber: "플레어 팁 마하수입니다. KOSHA 지침 적용범위는 0.2~0.5 입니다.",
  bsTransmissivity: "대기 투과율 τ(0~1)입니다. 모르면 1.0(감쇠 없음)을 쓰십시오.",
  bsAllowableRadiation: "이 지점에서 넘지 않아야 할 최대허용 복사열량 K 입니다(태양복사 포함, kcal/hr·m²). 사용자 사내 기준은 4,000 kcal/hr·m² 입니다.",
  bsBackgroundRadiation: "태양 복사 등 배경 복사량 K0 입니다(kcal/hr·m²). 실제 계산에는 K−K0 를 씁니다. ⚠ KOSHA 지침 원문 <부록 1> 자체의 공식 예시는 K0 를 빼지 않습니다(지침 문구: \"이는 설치지역의 최대 태양복사열을 포함한 수치임\" — 즉 K=4,000 이 이미 배경복사를 포함). 실제 여러 프로젝트 계산서는 여기에 더해 K0=860 을 추가로 빼는(더 보수적인, 즉 더 높은 스택을 요구하는) 관행을 쓰고 있어 기본값을 860 으로 뒀습니다 — 순수 지침 공식대로 하려면 0 으로 바꾸십시오.",
  bsFractionHeatRadiated: "방출 복사열 비율 F(0~1)입니다. KOSHA 지침 <표 2>에 해당 유체가 없으면 0.3을 씁니다.",
  bsCompositionMode: "'가스 조성(몰%)으로 입력'을 고르면 성분별 몰%에서 CL'(폭발하한계)·분자량·연소열량(HHV)을 자동으로 계산합니다. CL'은 단순 Le Chatelier 식이 아니라 KOSHA GUIDE P-179-2022(ISO 10156 기반) 방식으로, 불연성 성분마다 실제 억제력이 다른 것(질소등가계수 — CO2·SO2·HCl은 질소보다 더 억제, Ar·He는 덜 억제)까지 보정합니다. 이미 세 값을 알고 있으면 '직접 입력'을 쓰십시오.",
} as const;

export type FieldHelpKey = keyof typeof FIELD_HELP;
